import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  useColorModeValue,
  Text,
  Button,
  HStack,
  Badge,
  Divider,
  FormControl,
  Textarea,
  IconButton,
  Tooltip,
  useToast,
  Input,
  Select,
  NumberInput,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInputStepper,
  NumberInputField,
  CheckboxGroup,
  Checkbox,
  Stack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { Footer } from "../../components/layout/Footer";
import { FiSend, FiArrowLeft } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { Agent, AgentUsageMode, AIModel, Character } from "../../types";
import { agentsAPI } from "services/agents-api";
import { charactersAPI } from "services/characters-api";
import { aiAPI } from "services";

export const EditAgent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const [agent, setAgent] = useState<Agent | null>(null);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [arariaModels, setArariaModels] = useState<AIModel[]>([]);
  useEffect(() => {
    if (id) {
      loadAgent();
      loadCharacters();
      loadArariaModels();
    }
  }, [id]);
  const loadArariaModels = async () => {
    const arariaModels = await aiAPI.getModels();
    // filter models by capabilities (have to have chat and llm)
    const filteredModels = arariaModels.filter(
      (model) =>
        model.capabilities?.includes("chat") &&
        model.capabilities?.includes("tool-calling") &&
        (model.id?.includes("claude") ||
          model.id?.includes("gemini") ||
          model.id?.includes("gpt")) &&
        model.type === "llm"
    );
    setArariaModels(filteredModels);
  };
  const loadAgent = async () => {
    try {
      const data = await agentsAPI.getAgent(id!);
      setAgent(data);
    } catch (error) {
      console.error("Error loading agent:", error);
      toast({
        title: t("agents.error"),
        status: "error",
        duration: 3000,
      });
    }
  };

  const loadCharacters = async () => {
    try {
      const characters = await charactersAPI.getCharacters(1, 100);
      setCharacters(characters.characters);
    } catch (error) {
      toast({
        title: t("characters.error"),
        status: "error",
        duration: 3000,
      });
      console.error("Error loading characters:", error);
    }
  };
  const handleUpdateAgent = async () => {
    try {
      if (agent) {
        // remove userId property from agent
        delete agent.userId;
        delete agent.user;
        console.log(agent);
        await agentsAPI.updateAgent(id!, agent);
        toast({
          title: t("agents.updated"),
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating agent:", error);
      toast({
        title: t("agents.error"),
        status: "error",
        duration: 3000,
      });
    }
  };

  if (!agent) {
    return null;
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            {/* Header */}
            <HStack spacing={4}>
              <IconButton
                aria-label="Back to agents"
                icon={<FiArrowLeft />}
                onClick={() => navigate("/agents")}
              />
              <VStack align="start" spacing={2} flex={1}>
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  bgGradient="linear(to-r, brand.primary, brand.secondary)"
                  bgClip="text"
                >
                  {agent.name}
                </Heading>
                <HStack spacing={4}>
                  <Text color="gray.500">
                    {t("agents.created", {
                      date: new Date(agent.createdAt).toLocaleDateString(
                        i18n.language === "pt-BR" ? "pt-BR" : "en-US"
                      ),
                    })}
                  </Text>
                </HStack>
              </VStack>
            </HStack>

            {/* Agent Content */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              {/* Name */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.name")}</Heading>
                <Input
                  value={agent.name}
                  onChange={(e) => setAgent({ ...agent, name: e.target.value })}
                />
              </VStack>

              {/* Description */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.description")}</Heading>
                <Textarea
                  value={agent.description}
                  onChange={(e) =>
                    setAgent({ ...agent, description: e.target.value })
                  }
                />
              </VStack>

              {/* System Prompt */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.systemPrompt")}</Heading>
                <Textarea
                  value={agent.systemPrompt}
                  onChange={(e) =>
                    setAgent({ ...agent, systemPrompt: e.target.value })
                  }
                />
              </VStack>

              {/* Greeting */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.greeting")}</Heading>
                <Textarea
                  value={agent.greeting}
                  onChange={(e) =>
                    setAgent({ ...agent, greeting: e.target.value })
                  }
                />
              </VStack>

              {/* Profile Picture */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.profilePicture")}</Heading>
                <Input
                  value={agent.profilePicture}
                  onChange={(e) =>
                    setAgent({ ...agent, profilePicture: e.target.value })
                  }
                />
              </VStack>
              {/* Model */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.model")}</Heading>
                <Select
                  value={agent.modelName}
                  onChange={(e) =>
                    setAgent({ ...agent, modelName: e.target.value })
                  }
                >
                  {arariaModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </Select>
              </VStack>

              {/* Temperature */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.temperature")}</Heading>
                <NumberInput
                  value={agent.temperature}
                  onChange={(valueString) => {
                    const value = parseFloat(valueString);
                    if (!isNaN(value) && value >= 0 && value <= 1) {
                      setAgent({ ...agent, temperature: value });
                    } else {
                      setAgent({ ...agent, temperature: 0.5 });
                    }
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </VStack>

              {/* Tools */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.tools")}</Heading>
                <CheckboxGroup
                  onChange={(value) =>
                    setAgent({ ...agent, tools: value as string[] })
                  }
                  value={agent.tools}
                >
                  <Stack direction="column" spacing={2}>
                    <Checkbox value="SEARXNG">SearxNG</Checkbox>
                    <Checkbox value="GET_TODAYS_DATE">Get Todays Date</Checkbox>
                    <Checkbox value="SAVE_LEAD_TO_CSV">Save Lead To CSV</Checkbox>
                    <Checkbox value="CALCULATOR">Calculator</Checkbox>
                    <Checkbox value="IMAGE_GENERATION">
                      Image Generation
                    </Checkbox>
                    <Checkbox value="WEB_SCRAPER">Web Scraper</Checkbox>
                    <Checkbox value="GET_GEOCODE">Get Geocode</Checkbox>
                    <Checkbox value="GET_STREET_NAME_FROM_COORDINATES">
                      Get Street Name From Coordinates
                    </Checkbox>
                    {characters.map((character) => (
                      <Checkbox key={character.id} value={character.id}>
                        {t("agents.character")}: {character.name}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </VStack>
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("agents.usageMode")}</Heading>
                <Select
                  value={agent.usageMode}
                  onChange={(e) =>
                    setAgent({
                      ...agent,
                      usageMode: e.target.value as AgentUsageMode,
                    })
                  }
                >
                  <option value={AgentUsageMode.ANONYMOUS}>
                    {t("agents.anonymous")}
                  </option>
                  <option value={AgentUsageMode.AUTHENTICATED}>
                    {t("agents.authenticated")}
                  </option>
                </Select>
              </VStack>
            </VStack>
          </VStack>
          <Button onClick={handleUpdateAgent}>{t("agents.update")}</Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
